import React, { useContext, useState, useEffect } from 'react';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { User, Mail, Shield, Edit } from 'lucide-react';

const Profile = () => {
  const { user } = useContext(AuthContext);
  const [children, setChildren] = useState([]);

  useEffect(() => {
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      const response = await API.get('/children');
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to load children:', error);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-4xl font-bold mb-2">Profile</h1>
        <p className="text-muted-foreground">Manage your account settings</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold">
                {user?.name?.[0]}
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold">{user?.name}</h2>
              <p className="text-muted-foreground">{user?.email}</p>
              <Badge className="mt-2 capitalize">{user?.role}</Badge>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <div className="flex gap-2 mt-2">
                <Input value={user?.name || ''} disabled />
                <Button variant="outline" size="icon">
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <div className="flex gap-2 mt-2">
                <Input value={user?.email || ''} disabled />
                <Button variant="outline" size="icon">
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Children Profiles</CardTitle>
          <CardDescription>Manage your children's information</CardDescription>
        </CardHeader>
        <CardContent>
          {children.length > 0 ? (
            <div className="space-y-4">
              {children.map(child => (
                <Card key={child.child_id} className="bg-gray-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{child.name}</h3>
                        <p className="text-sm text-muted-foreground">{child.age} years old</p>
                        {child.focus_areas && child.focus_areas.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {child.focus_areas.map((area, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {area}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button variant="outline" size="icon">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No children profiles yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
